import React from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Avatar,
  Divider,
  Alert,
  Card,
  CardContent
} from '@mui/material';
import {
  HourglassEmpty,
  Email,
  ExitToApp,
  CheckCircleOutline
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const PendingApproval = () => {
  const { user, logout } = useAuth();

  // Get contact email from environment or use default
  const contactEmail = process.env.REACT_APP_ADMIN_CONTACT_EMAIL || 'admin@example.com';

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleContactAdmin = () => {
    window.location.href = `mailto:${contactEmail}?subject=Account Approval Request&body=Hello,%0D%0A%0D%0AI would like to request approval for my account on the Short Story Platform.%0D%0A%0D%0AAccount Details:%0D%0AEmail: ${user?.email}%0D%0AName: ${user?.name}%0D%0A%0D%0AThank you!`;
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={8}
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)'
          }}
        >
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                mx: 'auto',
                mb: 2,
                bgcolor: 'warning.main'
              }}
            >
              <HourglassEmpty sx={{ fontSize: 40 }} />
            </Avatar>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
              Account Pending Review
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
              Your account is awaiting administrator approval
            </Typography>
          </Box>

          {/* User Info */}
          {user && (
            <Card sx={{ mb: 4, bgcolor: 'grey.50' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                  <Avatar
                    src={user.picture}
                    sx={{ width: 48, height: 48, mr: 2 }}
                  >
                    {user.name?.charAt(0) || user.email?.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="h6">{user.name || 'User'}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {user.email}
                    </Typography>
                  </Box>
                </Box>
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Your account has been created successfully, but requires administrator approval before you can access the platform.
                  </Typography>
                </Alert>
              </CardContent>
            </Card>
          )}

          {/* Instructions */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
              <CheckCircleOutline sx={{ mr: 1, color: 'success.main' }} />
              What happens next?
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
              An administrator will review your account and approve access to the Short Story Platform. 
              This usually takes 1-2 business days. You'll receive an email notification once your account is approved.
            </Typography>
            
            <Divider sx={{ my: 3 }} />
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Need faster approval or have questions? Contact our administrator:
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<Email />}
              onClick={handleContactAdmin}
              sx={{
                bgcolor: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.dark'
                }
              }}
            >
              Contact Administrator
            </Button>
            <Button
              variant="outlined"
              startIcon={<ExitToApp />}
              onClick={handleLogout}
              sx={{
                borderColor: 'grey.400',
                color: 'grey.700',
                '&:hover': {
                  borderColor: 'grey.600',
                  bgcolor: 'grey.50'
                }
              }}
            >
              Sign Out
            </Button>
          </Box>

          {/* Footer */}
          <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="caption" color="text.secondary">
              Thank you for your patience. We're excited to have you join our community of writers!
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PendingApproval;
